// errorTracking.js
// Purpose: Track JavaScript errors and unhandled promise rejections to Supabase.
// - Global error handlers capture runtime crashes
// - Records to user_activity_events with event_type: error_occurred | unhandled_rejection
// - Non-blocking: errors in tracking itself are silently ignored

import { supabase } from '../config/supabase';
import { getAppSessionId, getUuidSessionId } from './activityEvents';
import logger from './logger';

/**
 * Build standard client metadata for error events.
 * @returns {Record<string, any>}
 */
function getClientMetadata() {
  return {
    platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    locale:
      (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
      'en-US',
    app_session_id: getAppSessionId(),
    app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
  };
}

/**
 * Get the current user ID from window.currentUser (set by AuthContext).
 * Returns null if no user is signed in.
 * @returns {string|null}
 */
function getCurrentUserId() {
  try {
    // @ts-ignore - currentUser set by AuthContext on sign-in
    return window.currentUser?.id || null;
  } catch {
    return null;
  }
}

/**
 * Record an error event to user_activity_events.
 * @param {'error_occurred' | 'unhandled_rejection' | 'api_error'} eventType
 * @param {Record<string, any>} meta - Error details (message, stack, etc.)
 */
async function recordError(eventType, meta) {
  try {
    const userId = getCurrentUserId();
    // If no user signed in, skip (we can't attribute the error)
    if (!userId) return;

    const client = getClientMetadata();
    // Remove undefined app_version to keep payload clean
    if (client.app_version === undefined) delete client.app_version;

    const event = {
      user_id: userId,
      session_id: getUuidSessionId(),
      event_type: eventType,
      client,
      meta: {
        ...meta,
        route: typeof window !== 'undefined' ? window.location?.pathname : '',
        url: typeof window !== 'undefined' ? window.location?.href : '',
      },
    };

    // Best-effort insert; don't retry or block on errors
    const { error } = await supabase.from('user_activity_events').insert([event]);

    if (error) {
      // Log locally but don't throw; tracking failures shouldn't affect app
      logger.warn('errorTracking: failed to record', eventType, error);
    }
  } catch (e) {
    // Silently ignore tracking errors to prevent infinite loops
    logger.debug?.('errorTracking: unexpected error', e);
  }
}

/**
 * Handle global window.onerror events (uncaught JS errors).
 * @param {string | Event} message
 * @param {string} [source]
 * @param {number} [lineno]
 * @param {number} [colno]
 * @param {Error} [error]
 */
function handleWindowError(message, source, lineno, colno, error) {
  recordError('error_occurred', {
    error_message: typeof message === 'string' ? message : error?.message || 'Unknown error',
    error_stack: error?.stack || null,
    error_name: error?.name || null,
    source_file: source || null,
    line: lineno || null,
    column: colno || null,
  });
  // Return false to allow default browser error handling
  return false;
}

/**
 * Handle unhandledrejection events (uncaught Promise rejections).
 * @param {PromiseRejectionEvent} event
 */
function handleUnhandledRejection(event) {
  const reason = event.reason;
  recordError('unhandled_rejection', {
    error_message: reason?.message || String(reason) || 'Unhandled promise rejection',
    error_stack: reason?.stack || null,
    error_name: reason?.name || null,
  });
}

/**
 * Track an API error manually (call from fetch wrappers or service layers).
 * @param {string} url - The API endpoint that failed
 * @param {number} status - HTTP status code
 * @param {string} [statusText] - HTTP status text
 * @param {string} [errorMessage] - Additional error message
 */
export function trackApiError(url, status, statusText, errorMessage) {
  recordError('api_error', {
    url,
    status,
    status_text: statusText || null,
    error_message: errorMessage || null,
  });
}

/**
 * Track an error manually (for try-catch blocks in critical paths).
 * @param {Error | string} error - The error to track
 * @param {Record<string, any>} [context] - Additional context
 */
export function trackError(error, context = {}) {
  const isError = error instanceof Error;
  recordError('error_occurred', {
    error_message: isError ? error.message : String(error),
    error_stack: isError ? error.stack : null,
    error_name: isError ? error.name : null,
    ...context,
  });
}

/**
 * Initialize global error handlers.
 * Call this once at app startup (in index.js).
 */
export function initErrorTracking() {
  if (typeof window === 'undefined') return;

  // Avoid double-initialization
  // @ts-ignore
  if (window.__timegenie_error_tracking_initialized) return;
  // @ts-ignore
  window.__timegenie_error_tracking_initialized = true;

  // Global JS error handler
  window.onerror = handleWindowError;

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  logger.info('🛡️ Error tracking initialized');
}
